import React, { useEffect } from 'react';
import { Provider } from 'react-redux';

import { BrowserRouter, Routes, Route  } from 'react-router-dom'
import { ThemeSwitcherProvider } from 'react-css-theme-switcher';
import { SubscriptionProvider } from 'contexts/SubscriptionContext';
import { LogoutProvider } from 'contexts/LogoutContext';
import { MobileNavigationProvider } from 'contexts/MobileNavigationContext';
import store from './store';
import history from './history'
import Layouts from './layouts'
import EmbedRoute from './routes/EmbedRoute';
import ChatbotEmbed from './views/embed-views/ChatbotEmbed';
import ChatAgentIframe from './views/app-views/chat-agent-iframe/ChatAgentIframe';
import { THEME_CONFIG } from './configs/AppConfig';
import KeyboardShortcutsProvider from './components/shared/KeyboardShortcutsProvider';
import './lang'
import './assets/css/mobile-top-nav.css';
import { SEC_AUTH_M_TK } from 'constants/AuthConstant';
import { decodeToken } from 'utils/jwtUtils';
import { clearAllLocalStorage, clearAllSessionStorage } from 'utils/logoutUtils';


const themes = {
  dark: `${process.env.PUBLIC_URL}/css/dark-theme.css`,
  light: `${process.env.PUBLIC_URL}/css/light-theme.css`,
};



function App() {
  const resetAuthStateSafely = () => {
    try {
      clearAllLocalStorage();
      clearAllSessionStorage();
      localStorage.removeItem(SEC_AUTH_M_TK);
    } catch (_storageError) {
      // no-op
    }
  };

  // Production console log removal (Stack Overflow/Dev.to recommended solution)
  // This is the #1 web-recommended approach with 8k+ views and high upvotes
  if (process.env.NODE_ENV === 'production') {
    // eslint-disable-next-line no-console
    console.log = function () {};
    // eslint-disable-next-line no-console
    console.debug = function () {};
    // eslint-disable-next-line no-console
    console.info = function () {};
    // Keep console.error and console.warn for production debugging
  }

  // Safari can retain stale token/storage state between sessions.
  // Reset auth state early when token is malformed or expired to avoid route/loading loops.
  useEffect(() => {
    try {
      const token = localStorage.getItem(SEC_AUTH_M_TK);
      if (!token) return;

      const decoded = decodeToken(token);
      const nowInSeconds = Math.floor(Date.now() / 1000);
      const isExpired = Boolean(decoded?.exp) && decoded.exp <= nowInSeconds;

      if (!decoded || isExpired) {
        resetAuthStateSafely();
        if (window.location.pathname !== '/auth/login') {
          window.location.replace('/auth/login');
        }
      }
    } catch (_error) {
      // If storage access fails (browser privacy mode quirks), force a clean unauthenticated state.
      resetAuthStateSafely();
      if (window.location.pathname !== '/auth/login') {
        window.location.replace('/auth/login');
      }
    }
  }, []);

  // Initialize app on startup
  useEffect(() => {
    // Comprehensive scrolling fix
    const applyComprehensiveScrollingFix = () => {
      try {
        // Fix the main content area
        const contentElements = document.querySelectorAll('.ant-layout-content, .ant-layout-content.h-100');
        contentElements.forEach(el => {
          el.style.setProperty('height', 'auto', 'important');
          el.style.setProperty('min-height', 'auto', 'important');
          el.style.setProperty('overflow', 'visible', 'important');
          el.style.setProperty('overflow-y', 'visible', 'important');
        });

        // Fix layout containers but preserve footer positioning
        const outerLayoutElements = document.querySelectorAll('.ant-layout:first-child');
        outerLayoutElements.forEach(el => {
          el.style.setProperty('height', 'auto', 'important');
          el.style.setProperty('min-height', '100vh', 'important');
          el.style.setProperty('overflow', 'visible', 'important');
          el.style.setProperty('overflow-y', 'visible', 'important');
        });

        const nestedLayoutElements = document.querySelectorAll('.ant-layout:not(:first-child)');
        nestedLayoutElements.forEach(el => {
          el.style.setProperty('height', 'auto', 'important');
          el.style.setProperty('min-height', 'auto', 'important');
          el.style.setProperty('overflow', 'visible', 'important');
          el.style.setProperty('overflow-y', 'visible', 'important');
        });

        // Fix CSS-in-JS styled elements that might constrain height
        const styledElements = document.querySelectorAll('[class*="css-"]');
        styledElements.forEach(el => {
          const computedStyle = getComputedStyle(el);
          if (computedStyle.minHeight && computedStyle.minHeight.includes('100vh')) {
            el.style.setProperty('min-height', 'auto', 'important');
            el.style.setProperty('height', 'auto', 'important');
            el.style.setProperty('overflow', 'visible', 'important');
          }
        });

        // Fix body and html scrolling
        document.body.style.setProperty('overflow-y', 'auto', 'important');
        document.body.style.setProperty('height', 'auto', 'important');
        document.documentElement.style.setProperty('overflow-y', 'auto', 'important');
        document.documentElement.style.setProperty('height', 'auto', 'important');

        // Fix root element
        const rootElement = document.getElementById('root');
        if (rootElement) {
          rootElement.style.setProperty('height', 'auto', 'important');
          rootElement.style.setProperty('overflow', 'visible', 'important');
        }

        // Fix App container
        const appElement = document.querySelector('.App');
        if (appElement) {
          appElement.style.setProperty('height', 'auto', 'important');
          appElement.style.setProperty('overflow', 'visible', 'important');
        }

        // Apply scrolling fixes
      } catch (error) {
        // Silent handling - runtime console disabler will manage any console output
      }
    };

    // Apply fixes immediately and after DOM changes
    applyComprehensiveScrollingFix();
    const timer1 = setTimeout(applyComprehensiveScrollingFix, 100);
    const timer2 = setTimeout(applyComprehensiveScrollingFix, 500);
    const timer3 = setTimeout(applyComprehensiveScrollingFix, 1000);

    // Set up mutation observer to reapply fixes when DOM changes
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        applyComprehensiveScrollingFix();
        scheduled = false;
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Cleanup function
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      observer.disconnect();
    };
  }, []);

  // Check if this is an embed route
  const isEmbedRoute = window.location.pathname.startsWith('/embed') ||
                       window.location.pathname.startsWith('/chatagent/chat') ||
                       window.location.pathname.startsWith('/chatagent/iframechat');

  return (
    <div className="App">
      <Provider store={store}>
        <BrowserRouter history={history}>
          {isEmbedRoute ? (
            // Minimal layout for embed routes
            <Routes>
              <Route path="/embed/*" element={<EmbedRoute />} />
              <Route path="/chatagent/chat" element={<ChatbotEmbed />} />
              <Route path="/chatagent/iframechat" element={<ChatAgentIframe />} />
            </Routes>
          ) : (
            // Full layout for main app
            <ThemeSwitcherProvider
              themeMap={themes}
              defaultTheme={THEME_CONFIG.currentTheme}
              insertionPoint="styles-insertion-point"
            >
              <LogoutProvider>
                <SubscriptionProvider>
                  <MobileNavigationProvider>
                    <KeyboardShortcutsProvider>
                      <Layouts />
                    </KeyboardShortcutsProvider>
                  </MobileNavigationProvider>
                </SubscriptionProvider>
              </LogoutProvider>
            </ThemeSwitcherProvider>
          )}
        </BrowserRouter>
      </Provider>
    </div>
  );
}

export default App;
